/**
 * Recommend Engine (AI Advisory P3) — DETERMINISTIC decision tree.
 *
 * No LLM here by design: the LLM's job ended at P2 (graded impacts). Decisions
 * are code — reproducible, unit-testable, cheap, and every rationale is
 * templated from the actual firing inputs (real numbers, never generated prose).
 *
 * Inputs:  P2 impacts (surfaced, conf >= 0.6) · open trades (SL/TP proximity)
 *          · riskScore() band/components · TA cache · live prices/exposures
 * Output:  recommendations in the existing S.aiCopilot.recommendations schema
 *          (type/ticker/urgency/title/reasoning/action/metrics) + engine:true,
 *          ranked money-saving-first (blueprint §3: risk is a veto, SET SL and
 *          TP-locking outrank deploying cash).
 *
 * Rules v1 (trigger → action):
 *   R1 SL_MISSING      open trade without SL            → stop_loss (set default SL)
 *   R2 TP_PROXIMITY    price >= 92% of SL→TP1 range     → take_profit
 *   R3 BEARISH_IMPACT  surfaced bearish news impact      → sell (trim/review, evidence-quoted)
 *   R4 BULLISH_DCA     surfaced bullish impact + pos <-10% → dca_adjust (RISK-VETOED)
 *   R5 CONFLICT        bullish AND bearish on same ticker → alert (both sides shown)
 *   R6 CONCENTRATION   risk worst = concentration, band != Healthy → rebalance
 *   R7 FX_OVERLAY      TRY-heavy exposure + material 10%-depreciation damage → alert
 *                      (audit 2026-07-06 #12: the 0.6 conf gate structurally mutes
 *                      FX/macro news, so FX risk is watched structurally instead)
 *
 * Risk veto: money-deploying actions (R4) are downgraded to an alert when the
 * risk band is High, and marked low-urgency when Elevated. Conflicts never
 * produce a directional action (R5). "No recommendation" is a valid output.
 */

import { S, save } from '../core/state.js';
import { hVal, hTotUSD } from '../core/math.js';
import { toUSD, uid } from '../core/utils.js';
import { ledgerRecEmitted, ledgerMergeDiff } from '../core/decision-ledger.js';
import { transformForMandate } from '../core/alpha-score.js';
import { riskScore, riskFxExposure, stressTest, defaultStopAdverse, effectiveStop } from '../core/risk-calc.js';
import { getImpacts, exposureUSD } from './ai-impact.js';

const MAX_ENGINE_RECS = 10;
const TP_PROXIMITY_TRIGGER = 0.92;
const DCA_DRAWDOWN_TRIGGER = -10;   // position P&L % below which averaging-down is considered
// The rule now lives in core/risk-calc.js (HORIZ-002). W2-2's leverage cap was applied to R1
// below and NOT to R2, so this same file disagreed with itself.

// Category priority: money-saving first (lower = earlier). Blueprint §3 ranking.
const PRIORITY = { stop_loss: 1, take_profit: 2, sell: 3, alert: 4, rebalance: 5, dca_adjust: 6 };

/** % weight of a ticker within priced holdings value (0 when unpriced/empty). */
function _weightPct(ticker, holdings, prices, fx) {
  let total = 0, mine = 0;
  (holdings || []).forEach(h => {
    const v = hVal(h).vU || 0;
    total += v;
    if (h.ticker === ticker) mine += v;
  });
  void prices; void fx;
  return total > 0 ? +(mine / total * 100).toFixed(1) : 0;
}

/** Position P&L% for a held ticker (value vs invested via hTotUSD), null when unpriced. */
function _pnlPct(ticker, holdings) {
  let inv = 0, val = 0;
  (holdings || []).forEach(h => {
    if (h.ticker !== ticker) return;
    const v = hVal(h);
    if (v.vU) { val += v.vU; inv += hTotUSD(h) || 0; }
  });
  if (!inv) return null;
  return +((val - inv) / inv * 100).toFixed(1);
}

/**
 * Pure rule evaluation over an injected context — exported for unit tests.
 * @param {Object} ctx {impacts, trades, holdings, prices, risk, now}
 * @returns {Array} engine recommendations (unstamped)
 */
export function _engineRules(ctx) {
  const { impacts = [], trades = [], holdings = [], prices = {}, risk } = ctx;
  const recs = [];
  const band = risk?.band || 'Healthy';
  const openTrades = trades.filter(t => t.status !== 'Closed' && t.type !== 'TimeDeposit');

  // ── R1: open trades without SL — the cheapest money saved ──
  // Leverage-aware (audit W2-2): at 3-5x a flat 25% stop sits AT/BELOW the
  // liquidation point — a stop that can never execute. The suggested adverse
  // distance is min(25%, 70%/lev): comfortably inside liquidation with a 30%
  // buffer. Capital = margin (entry×qty); notional is labeled as notional.
  openTrades.filter(t => t.sl == null).forEach(t => {
    const lev = t.leverage || 1;
    const adverse = defaultStopAdverse(lev);
    const slDefault = +(t.side === 'Long' ? t.entry * (1 - adverse) : t.entry * (1 + adverse)).toFixed(4);
    const marginUSD = Math.round(toUSD((t.entry || 0) * (t.qty || 0), t.currency || 'USD'));
    const notionalUSD = Math.round(marginUSD * lev);
    const liqPrice = lev > 1 ? +(t.side === 'Long' ? t.entry * (1 - 1 / lev) : t.entry * (1 + 1 / lev)).toFixed(4) : null;
    recs.push({
      type: 'stop_loss', ticker: t.ticker,
      urgency: lev > 1 ? 'high' : 'medium',
      title: `${t.ticker}: no stop loss${lev > 1 ? ` at ${lev}x leverage` : ''}`,
      reasoning: `Open ${t.side} (${lev}x) has no SL — $${marginUSD.toLocaleString('en-US')} margin capital${lev > 1 ? ` ($${notionalUSD.toLocaleString('en-US')} notional)` : ''} unprotected.${liqPrice ? ` Liquidation ≈ ${liqPrice}; suggested stop sits safely inside it.` : ''} Rule: ${Math.round(adverse * 100)}% adverse from entry.`,
      action: `Set SL at ${slDefault} (${t.currency || 'USD'})`,
      metrics: { suggestedSL: slDefault, leverage: lev, marginUSD, notionalUSD, liqPrice, adversePct: Math.round(adverse * 100) },
      _score: 90 + (lev > 1 ? 8 : 0),
    });
  });

  // ── R2: TP1 proximity — lock gains ──
  openTrades.filter(t => t.tp1 != null && prices[t.ticker]?.price != null).forEach(t => {
    const p = prices[t.ticker].price;
    const slEff = effectiveStop(t);
    const dom = t.tp1 - slEff;
    const prox = dom ? Math.max(0, Math.min((p - slEff) / dom, 1)) : 0;
    if (prox < TP_PROXIMITY_TRIGGER) return;
    const gainPct = t.entry ? +(((t.side === 'Long' ? t.tp1 - t.entry : t.entry - t.tp1) / t.entry) * 100 * (t.leverage || 1)).toFixed(1) : null;
    recs.push({
      type: 'take_profit', ticker: t.ticker,
      urgency: 'high',
      title: `${t.ticker} at ${Math.round(prox * 100)}% of range to TP1`,
      reasoning: `Price ${p} vs TP1 ${t.tp1} — target within reach${gainPct != null ? `; locking here realizes ~${gainPct}% on capital` : ''}. Money saved beats money hoped.`,
      action: `Apply TP1 at ${t.tp1}${t.tp1Qty ? ` for ${t.tp1Qty} units` : ''}`,
      metrics: { proximity: +prox.toFixed(2), tp1: t.tp1, price: p },
      _score: 82 + prox * 10,
    });
  });

  // ── Impact-driven rules: group surfaced impacts per ticker ──
  const byTicker = new Map();
  impacts.filter(i => i.surfaced).forEach(im => {
    im.tickers.forEach(t => {
      if (!byTicker.has(t)) byTicker.set(t, []);
      byTicker.get(t).push(im);
    });
  });

  byTicker.forEach((ims, ticker) => {
    const bear = ims.filter(i => i.direction === 'bearish');
    const bull = ims.filter(i => i.direction === 'bullish');

    // ── R5: conflict → SET ALERT, show both sides, no directional call ──
    if (bear.length && bull.length) {
      const b1 = bull.sort((a, b) => b.confidence - a.confidence)[0];
      const b2 = bear.sort((a, b) => b.confidence - a.confidence)[0];
      recs.push({
        type: 'alert', ticker,
        urgency: 'medium',
        title: `${ticker}: conflicting news signals`,
        reasoning: `Bullish (${b1.confidence}): "${b1.evidenceQuote}" vs bearish (${b2.confidence}): "${b2.evidenceQuote}". Conflict → no directional action; watch instead.`,
        action: 'Set alert and re-evaluate on next scan',
        metrics: { bullConf: b1.confidence, bearConf: b2.confidence },
        _score: 60,
      });
      return; // conflict suppresses R3/R4 for this ticker
    }

    const weight = _weightPct(ticker, holdings, prices, S.fxRate);
    const pnl = _pnlPct(ticker, holdings);
    const exp = exposureUSD(ticker);

    // ── R3: bearish impact → trim/review ──
    if (bear.length) {
      const top = bear.sort((a, b) => b.confidence - a.confidence)[0];
      const heavy = top.magnitude === 'high' && top.confidence >= 0.8;
      recs.push({
        type: 'sell', ticker,
        urgency: heavy ? 'high' : 'medium',
        title: `${ticker}: bearish ${top.linkType.replace('_', '/')} news — ${heavy ? 'reduce exposure' : 'review position'}`,
        reasoning: `"${top.evidenceQuote}" (${top.source}, conf ${top.confidence}, ${top.magnitude})${weight ? ` · position ${weight}% of holdings` : ''}${band !== 'Healthy' ? ` · portfolio risk ${band}` : ''} — $${exp.toLocaleString('en-US')} exposed.`,
        action: heavy ? `Trim ${ticker} and reassess remainder` : `Review ${ticker}; set alert at key level if holding`,
        metrics: { confidence: top.confidence, magnitude: top.magnitude, weightPct: weight, exposureUSD: exp },
        _score: 65 + top.confidence * 20 + (top.magnitude === 'high' ? 8 : top.magnitude === 'med' ? 4 : 0),
      });
    }

    // ── R4: bullish impact + drawdown → DCA candidate (risk-vetoed) ──
    if (bull.length && pnl != null && pnl <= DCA_DRAWDOWN_TRIGGER) {
      const top = bull.sort((a, b) => b.confidence - a.confidence)[0];
      if (top.confidence < 0.7) return;
      if (band === 'High') {
        recs.push({
          type: 'alert', ticker,
          urgency: 'low',
          title: `${ticker}: DCA signal blocked by risk`,
          reasoning: `Bullish "${top.evidenceQuote}" (conf ${top.confidence}) with position at ${pnl}%, but portfolio risk is High — deploying vetoed. Fix risk first.`,
          action: 'Resolve risk drivers before averaging down',
          metrics: { confidence: top.confidence, pnlPct: pnl, vetoed: true },
          _score: 40,
        });
      } else {
        recs.push({
          type: 'dca_adjust', ticker,
          urgency: band === 'Elevated' ? 'low' : 'medium',
          title: `${ticker}: averaging-down candidate on bullish news`,
          reasoning: `Position at ${pnl}% with bullish "${top.evidenceQuote}" (${top.source}, conf ${top.confidence})${band === 'Elevated' ? ' · risk Elevated — small size only' : ''}.`,
          action: `Evaluate DCA into ${ticker} within monthly plan limits`,
          metrics: { confidence: top.confidence, pnlPct: pnl, weightPct: weight },
          _score: 45 + top.confidence * 15,
        });
      }
    }
  });

  // ── R6: concentration is the worst risk component and band is not Healthy ──
  if (risk && risk.band !== 'Healthy' && risk.worst && risk.worst.key === 'concentration') {
    recs.push({
      type: 'rebalance', ticker: '',
      urgency: risk.band === 'High' ? 'high' : 'medium',
      title: 'Concentration is your worst risk driver',
      reasoning: `Risk ${risk.band} (score ${risk.overall}/100); weakest component: ${risk.worst.note}. Rebalancing reduces single-name damage.`,
      action: 'Open Rebalance review',
      metrics: { riskScore: risk.overall, band: risk.band },
      _score: 55,
    });
  }

  // ── R7: FX overlay — TRY-heavy book with material depreciation damage ──
  // Deterministic: fires from FX concentration + stress numbers, independent of
  // news flow (which the confidence gate structurally mutes for FX/macro).
  const fxo = ctx.fx;
  if (fxo && fxo.topCcy === 'TRY' && fxo.topPct >= 55 && fxo.stress10Usd >= 300) {
    const sevHigh = fxo.topPct >= 75 || fxo.stress10Pct >= 6;
    recs.push({
      type: 'alert', ticker: '',
      urgency: sevHigh ? 'high' : 'medium',
      title: `FX overlay: ${Math.round(fxo.topPct)}% of exposure sits in TRY`,
      reasoning: `A 10% TRY depreciation costs ~$${Math.round(fxo.stress10Usd).toLocaleString('en-US')} (${fxo.stress10Pct.toFixed(1)}% of net worth). FX/macro news rarely clears the confidence gate, so this overlay watches the currency risk structurally.`,
      action: 'Review currency exposure — consider steering new contributions to USD assets',
      metrics: { fxTopCcy: fxo.topCcy, fxTopPct: +fxo.topPct.toFixed(1), stress10Usd: Math.round(fxo.stress10Usd), stress10Pct: +fxo.stress10Pct.toFixed(1) },
      _score: 55 + (sevHigh ? 10 : 0),
    });
  }

  // Rank: category priority first (money-saving up), then score
  return recs
    .sort((a, b) => (PRIORITY[a.type] || 9) - (PRIORITY[b.type] || 9) || b._score - a._score)
    .slice(0, MAX_ENGINE_RECS);
}

/**
 * Run the engine over live state and MERGE into S.aiCopilot.recommendations
 * (engine recs first; LLM recs for the same ticker+type are replaced).
 * @returns {{ok:boolean, recs:Array}}
 */
export function runRecommendEngine() {
  let risk = null;
  try { risk = riskScore(); } catch (_e) { /* risk optional */ }

  // Staleness gate (audit W1-2): if the impact stage failed this scan, stored
  // impacts are OLD — re-running impact rules over them would re-stamp stale
  // evidence as fresh recommendations. Older than 6h → impact rules skip.
  const IMPACT_FRESH_MS = 6 * 60 * 60 * 1000;
  const impTs = S.aiCopilot && S.aiCopilot.lastImpact && S.aiCopilot.lastImpact.ts;
  const impactsFresh = impTs && (Date.now() - Date.parse(impTs)) < IMPACT_FRESH_MS;
  if (impTs && !impactsFresh) console.warn('[ENGINE] Impacts stale (', impTs, ') — impact-driven rules skipped this run');

  // R7 inputs: FX concentration + standardized 10%-TRY-depreciation stress.
  let fx = null;
  try {
    const xb = riskFxExposure().breakdown[0];
    if (xb) {
      const st = stressTest(0, 10);
      fx = { topCcy: xb.ccy, topPct: xb.pct, stress10Usd: st.tryLoss, stress10Pct: st.base ? st.tryLoss / st.base * 100 : 0 };
    }
  } catch (_e) { /* fx overlay optional */ }

  const raw = _engineRules({
    impacts: impactsFresh ? getImpacts(true) : [],
    trades: S.trades || [],
    holdings: S.holdings || [],
    prices: S.prices || {},
    risk,
    fx,
  });

  const ts = new Date().toISOString();
  // Mandate policy applies to THIS pipeline too (gap found 2026-07-22: the engine path
  // bypassed transformForMandate — exit directives on accumulate assets leaked through).
  const mandateCtx = { holdings: S.holdings || [], portfolios: S.portfolios || [], trades: S.trades || [], prices: S.prices || {}, fxRate: S.fxRate };
  const stamped = raw.map(r => {
    const { _score, ...rec } = r;
    return transformForMandate({ id: uid(), ...rec, engine: true, engineScore: Math.round(_score), scanId: 'engine', createdAt: ts, dismissed: false }, mandateCtx);
  });

  if (!S.aiCopilot) S.aiCopilot = { recommendations: [], scenarios: [], alerts: [], lastScan: null, scanHistory: [] };
  const prevAll = (S.aiCopilot.recommendations || []).slice(); // decision-ledger: snapshot before merge
  const keys = new Set(stamped.map(r => `${r.ticker}:${r.type}`));
  const keptOld = (S.aiCopilot.recommendations || [])
    .filter(r => !r.dismissed && !r.acted) // acted = done; if the trigger persists, the fresh engine rec re-emits it
    .filter(r => !(r.engine && !keys.has(`${r.ticker}:${r.type}`)))  // stale engine recs die with their trigger
    .filter(r => !keys.has(`${r.ticker}:${r.type}`))
    .map(r => transformForMandate(r, mandateCtx)); // surviving recs obey the mandate too
  S.aiCopilot.recommendations = [...stamped, ...keptOld].slice(0, 20);
  ledgerRecEmitted(stamped);
  ledgerMergeDiff(prevAll, stamped, S.aiCopilot.recommendations);
  save();

  try { window.__spektrxEngine = { ts, recs: stamped }; } catch (_e) { /* non-browser */ }
  return { ok: true, recs: stamped };
}
